import React from 'react';
import { promiseStates, ReactNodeGenerator, dateFormat, msToHumanTime } from 'lib/helpers';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor, EditorEvent, Events } from 'tinymce';
import { EssayTeacherSceneStore } from './EssayTeacherSceneStore';
import { observer } from 'mobx-react';
import { PromiseMessage, routerStore } from 'lib/components';
import { httpClient } from 'domain/utils/httpClient';
import { environment } from 'lib/environment';
import Swal from 'sweetalert2';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import $ from 'jquery';
import Box from '@mui/material/Box';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import Countdown from 'react-countdown';
import moment from 'moment';
import { routes } from 'pages/Routes';


interface Props {
    id: number
}

type State = {
    acceptModal_isOpen: boolean;
    acceptModal_rating: number | null;
    acceptModal_comment: string | null;

    rejectModal_isOpen: boolean;
    rejectModal_comment: string | null;
}

@observer
export class EssayTeacherScene extends React.Component<Props, State> {
    readonly store: EssayTeacherSceneStore;
    private editor: TinyMCEEditor | null = null;

    state = {
        acceptModal_isOpen: false,
        acceptModal_rating: null,
        acceptModal_comment: null,

        rejectModal_isOpen: false,
        rejectModal_comment: null,
    };

    constructor(props: Readonly<Props>) {
        super(props);
        this.store = new EssayTeacherSceneStore(props.id);
    }

    componentDidMount() {
        this.store.loadEssay();
    }

    *renderer(): ReactNodeGenerator {
        const state = this.store.essayLoader;
        yield (
            <PromiseMessage key="lesson-loader-state"
                label="Loading essay"
                state={state}
                align="right" />);

        if (promiseStates.isRejected(state)) {
            yield (
                <div className="row" key="lesson-load-action">
                    <div className="col-auto ml-auto">
                        <button type="button" className="btn btn-primary"
                            onClick={this.store.loadEssay}>
                            Load essay
                        </button>
                    </div>
                </div>
            );
        }

        if (state == null || state.isResolved == false) {
            return;
        }

        yield (
            <Modal key="AcceptModal"
                isOpen={this.state.acceptModal_isOpen}
                size="lg"
                centered
                onOpened={this.onAcceptModalOpen}>
                <ModalHeader>
                    <div>Accept Essay</div>
                </ModalHeader>
                <ModalBody>
                    <Box
                        sx={{
                            '& > legend': { mt: 2 },
                        }}
                    >
                        <Typography component="legend">Please give rating</Typography>
                        <Rating
                            name="simple-controlled"
                            value={this.state.acceptModal_rating}
                            onChange={this.onAcceptRatingChange}
                            size="large"
                        />
                    </Box>

                    <Typography component="legend">Comment</Typography>
                    <textarea style={{ width: '100%', padding: '5px' }} onChange={this.onAcceptCommentChange}></textarea>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-primary"
                        onClick={this.onAcceptModalBtnClick} value="test">Accept Essay
                    </button>
                    <button type="button" className="btn btn-secondary"
                        onClick={this.onAcceptModalCancelBtnClick} value="test">Cancel
                    </button>
                </ModalFooter>
            </Modal>
        );

        yield (
            <Modal key="RejectModal"
                isOpen={this.state.rejectModal_isOpen}
                size="lg"
                centered
                onOpened={this.onRejectModalOpen}>
                <ModalHeader>
                    <div>Reject Essay</div>
                </ModalHeader>
                <ModalBody>
                    <Box
                        sx={{
                            '& > legend': { mt: 2 },
                        }}
                    >
                        <Typography component="legend">Comment</Typography>
                        <textarea style={{ width: '100%', padding: '5px' }} onChange={this.onRejectCommentChange}></textarea>
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-primary"
                        onClick={this.onRejectModalBtnClick} value="test">Reject
                    </button>
                    <button type="button" className="btn btn-secondary"
                        onClick={this.onRejectModalCancelBtnClick} value="test">Cancel
                    </button>
                </ModalFooter>
            </Modal>
        );

        const essay = state.value;
        let timeTaken: string | undefined = undefined;
        if (essay.StartedAt != null) {
            if (essay.FinishedAt != null) {
                const diff = moment.unix(essay.FinishedAt).diff(moment.unix(essay.StartedAt));
                timeTaken = msToHumanTime(diff);
            } else {
                const diff = moment().diff(moment.unix(essay.StartedAt));
                timeTaken = msToHumanTime(diff);
            }
        }
        const editorEnabled: boolean = essay.TeacherAcceptAllowed || essay.TeacherRejectAllowed;
        const editorDisabled = editorEnabled == false;
        const showAcceptBtn = essay.TeacherAcceptAllowed;
        const showRejectBtn = essay.TeacherRejectAllowed;

        yield (
            <div key="EssayTeacherScene">
                <h1>{essay.Title}</h1>
                <table className="table">
                    <tbody>
                        <tr>
                            <td>Student</td>
                            <td>Time limit</td>
                            <td>Time taken</td>
                            <td>Rating</td>
                            <td>Status</td>
                            <td>Updated at</td>
                        </tr>
                        <tr>
                            <td>{essay.StudentName}</td>
                            <td>{essay.TimeLimitStr}</td>
                            <td>{timeTaken}</td>
                            <td>
                                <Rating
                                    name="simple-controlled"
                                    value={essay.Rating}
                                    size="large"
                                    readOnly
                                />
                            </td>
                            <td>{essay.StatusStr}</td>
                            <td>{dateFormat(essay.UpdatedAt, 'wymdhm', true)}</td>
                        </tr>
                    </tbody>
                </table>
                <Editor
                    onInit={(evt, editor) => { this.onMceInit(editor) }}
                    initialValue={essay.Text ?? undefined}
                    disabled={editorDisabled}
                    init={{
                        height: 500,
                        menubar: false,
                        readonly: editorDisabled,
                        plugins: [
                            'advlist autolink lists link image charmap print preview anchor',
                            'searchreplace visualblocks code fullscreen',
                            'insertdatetime media table paste code help wordcount'
                        ],
                        toolbar: 'undo redo | bold italic strikethrough forecolor',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                />
                {showAcceptBtn &&
                    <button type="button" className="btn btn-primary" onClick={this.onAcceptBtnClick} style={{ 'marginRight': '15px' }}>Accept</button>
                }
                {showRejectBtn &&
                    <button type="button" className="btn btn-secondary" onClick={this.onRejectBtnClick} style={{ 'marginRight': '15px' }}>Edit</button>
                }
            </div>
        );
    }

    render() {
        return Array.from(this.renderer());
    }

    onMceInit(editor: TinyMCEEditor) {
        const self = this;
        self.editor = editor;
        const essay = self.store.essayLoader?.value;
        if (!essay) {
            return;
        }
        const canEdit = essay.TeacherAcceptAllowed || essay.TeacherRejectAllowed;
        if (canEdit == false) {
            Swal.fire({ icon: 'info', text: `Essay is readonly. Status: ${essay.StatusStr}` });
            return;
        }
    }

    /**
     * Accept Modal Start
     */

    onAcceptBtnClick = () => {
        var self = this;
        this.setState({ acceptModal_isOpen: true });
    }

    onAcceptModalOpen = () => {
        var self = this;
        this.setState({ acceptModal_comment: null, acceptModal_rating: null });
    }

    onAcceptModalBtnClick = async () => {
        var self = this;
        const rating = self.state.acceptModal_rating;
        if (rating == null) {
            Swal.fire({ icon: 'warning', text: 'You must rate the essay' });
            return;
        }
        const editor = self.editor;
        if (!editor) {
            return;
        }
        var editorContent = editor.getContent();
        await self.store.submitEssay(self.props.id, editorContent, true, rating, self.state.acceptModal_comment);
        await Swal.fire({ icon: 'success', text: 'Essay successfully accepted' });
        this.setState({ acceptModal_isOpen: false });
        routerStore.navigateTo(routes.EssayTeacherList);
    }

    onAcceptModalCancelBtnClick = () => {
        this.setState({ acceptModal_isOpen: false });
    }

    onAcceptRatingChange = (event: React.SyntheticEvent, value: number | null) => {
        this.setState({ acceptModal_rating: value });
    }

    onAcceptCommentChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        this.setState({ acceptModal_comment: e.currentTarget.value });
    }

    /**
     * Accept Modal End
     */



    onRejectBtnClick = async () => {
        var self = this;
        this.setState({ rejectModal_isOpen: true });
    }

    onRejectModalOpen = () => {
        var self = this;
        this.setState({ rejectModal_comment: null });
    }

    onRejectModalBtnClick = async () => {
        var self = this;
        const editor = self.editor;
        if (!editor) {
            return;
        }
        var editorContent = editor.getContent();
        await self.store.submitEssay(self.props.id, editorContent, false, null, self.state.rejectModal_comment);
        await Swal.fire({ icon: 'success', text: 'Essay successfully rejected' });
        this.setState({ rejectModal_isOpen: false });
        routerStore.navigateTo(routes.EssayTeacherList);
    }

    onRejectCommentChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        this.setState({ rejectModal_comment: e.currentTarget.value });
    }

    onRejectModalCancelBtnClick = () => {
        this.setState({ rejectModal_isOpen: false });
    }
}
